import { effect, inject, Injectable, untracked } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, skip } from 'rxjs';
import { ConsentService } from './consent.service';

export type TrackingData = {
  visitor_id: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer: string | null;
};

/** Stub of Meta's base code, fbevents.js replaces callMethod and drains the queue. */
type Fbq = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: Fbq;
  loaded: boolean;
  version: string;
};
type PixelWindow = Window & { fbq?: Fbq; _fbq?: Fbq };

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const STORAGE_KEY = 'lp_tracking';
const COOKIE = 'lp_vid';
const COOKIE_DAYS = 396; // ~13 months
const META_PIXEL_ID = '1484791023511042';

/** crypto.randomUUID() exists only in secure contexts (https, localhost), not on the http dev domain. */
function uuid(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * - UTM + referrer per session (sessionStorage, always)
 * - Visitor cookie for returning visitors, only with statistics consent
 * - Meta Pixel, only with marketing consent
 */
@Injectable({ providedIn: 'root' })
export class TrackingService {
  readonly #router = inject(Router);
  readonly #consent = inject(ConsentService);
  #visitorId: string | null = null;
  #returning = false;

  constructor() {
    // Consent can come later in the session, start as soon as it does
    effect(() => {
      if (this.#consent.statistics()) untracked(() => this.#startVisitor());
    });
    effect(() => {
      if (this.#consent.marketing()) untracked(() => this.#loadPixel());
    });
  }

  get isReturning(): boolean {
    return this.#returning;
  }

  captureEntry(): void {
    if (this.#consent.statistics()) this.#startVisitor();
    // Cookie from before the consent banner existed
    else if (this.#readCookie()) document.cookie = `${COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;

    // First touch of the session wins, later internal navigation has no UTM anyway
    const params = new URLSearchParams(window.location.search);
    const stored = this.#stored();
    const hasUtm = UTM_KEYS.some((key) => params.get(key));
    if (!stored || hasUtm) {
      const data: Record<string, string | null> = {
        referrer: document.referrer ? document.referrer.slice(0, 512) : (stored?.['referrer'] ?? null),
      };
      UTM_KEYS.forEach((key) => (data[key] = params.get(key)));
      this.#store(data);
    }
  }

  /** Meta Pixel: PageView of the entry page fires on load, SPA navigations here. */
  trackPageViews(): void {
    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        skip(1),
      )
      .subscribe(() => this.metaEvent('PageView'));
  }

  /** Meta Pixel standard event, no-op without consent or if the pixel is blocked. */
  metaEvent(name: string, params?: Record<string, unknown>): void {
    (window as PixelWindow).fbq?.('track', name, params);
  }

  data(): TrackingData {
    const stored = this.#stored() ?? {};
    return {
      visitor_id: this.#visitorId,
      utm_source: stored['utm_source'] ?? null,
      utm_medium: stored['utm_medium'] ?? null,
      utm_campaign: stored['utm_campaign'] ?? null,
      utm_content: stored['utm_content'] ?? null,
      utm_term: stored['utm_term'] ?? null,
      referrer: stored['referrer'] ?? null,
    };
  }

  #stored(): Record<string, string | null> | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  #store(data: Record<string, string | null>): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // storage blocked (private mode), tracking is best effort
    }
  }

  #startVisitor(): void {
    if (this.#visitorId) return;
    const existing = this.#readCookie();
    this.#returning = !!existing;
    this.#visitorId = existing ?? uuid();
    this.#writeCookie(this.#visitorId);
  }

  #loadPixel(): void {
    const w = window as PixelWindow;
    if (w.fbq) return;
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, args);
      else fbq.queue.push(args);
    } as Fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    w.fbq = w._fbq = fbq;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);

    fbq('init', META_PIXEL_ID);
    fbq('track', 'PageView');
  }

  #readCookie(): string | null {
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([^;]+)`));
    return match ? decodeURIComponent(match[1]) : null;
  }

  #writeCookie(value: string): void {
    const expires = new Date(Date.now() + COOKIE_DAYS * 86400000).toUTCString();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
  }
}
